import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  useTheme
} from '@mui/material';
import {
  Print,
  Speed,
  Palette,
  CheckCircle,
  ContactMail,
  Upload,
  Calculate,
  Build
} from '@mui/icons-material';
import { COLORS, withOpacity } from '../../theme/colors';
import SEOHead from '../../components/SEO/SEOHead';
import ThreeDPrintingQuote from '../../components/3DPrinting/ThreeDPrintingQuote';

const ThreeDPrinting = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <Print />,
      title: 'High-Quality 3D Printing',
      description: 'Professional-grade 3D printing with precision and detail for all your prototyping and production needs.'
    },
    {
      icon: <Speed />,
      title: 'Fast Turnaround',
      description: 'Quick delivery times without compromising on quality. Get your prints in days, not weeks.'
    },
    {
      icon: <Palette />,
      title: 'Multiple Materials',
      description: 'Wide range of materials including PLA, ABS, PETG, TPU, and specialty filaments for various applications.'
    },
    {
      icon: <Build />,
      title: 'Post-Processing',
      description: 'Professional finishing services including sanding, painting, and assembly for production-ready parts.'
    }
  ];

  const materials = [
    { name: 'PLA', description: 'Easy to print, biodegradable, perfect for prototypes' },
    { name: 'ABS', description: 'Strong and durable, ideal for functional parts' },
    { name: 'PETG', description: 'Chemical resistant, food-safe, crystal clear' },
    { name: 'TPU', description: 'Flexible and elastic, perfect for phone cases and gaskets' },
    { name: 'Wood Fill', description: 'Wood-like finish, can be stained and finished' },
    { name: 'Metal Fill', description: 'Heavy metallic finish, can be polished' }
  ];

  const applications = [
    'Rapid Prototyping',
    'Custom Parts & Components',
    'Architectural Models',
    'Educational Tools',
    'Art & Sculptures',
    'Functional Prototypes',
    'Replacement Parts',
    'Custom Fixtures & Jigs'
  ];

  return (
    <>
      <SEOHead
        title="3D Printing Services - Professional 3D Printing | The Random Store"
        description="Professional 3D printing services with instant quotes. Upload your STL files and get high-quality 3D prints with fast turnaround. Multiple materials available."
        keywords="3d printing services, professional 3d printing, rapid prototyping, custom 3d printing, stl printing, 3d printing india, instant 3d printing quote"
        url="https://therandomstores.com/services/3d-printing"
      />
      
      <Box sx={{ backgroundColor: COLORS.background.light, minHeight: '100vh' }}>
        {/* Hero Section */}
        <Box sx={{ backgroundColor: COLORS.background.paper, py: 8, mb: 6 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h2" fontWeight={700} color={COLORS.text.primary} gutterBottom>
                  Professional 3D Printing Services
                </Typography>
                <Typography variant="h6" color={COLORS.text.secondary} mb={4} sx={{ lineHeight: 1.6 }}>
                  From concept to reality. Get instant quotes, upload your STL files, and receive high-quality 3D prints with professional finishing.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    href="#quote-calculator"
                    sx={{ 
                      backgroundColor: COLORS.primary.main,
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600
                    }}
                  >
                    Get Instant Quote
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large"
                    href="#features"
                    sx={{ 
                      borderColor: COLORS.primary.main,
                      color: COLORS.primary.main,
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem'
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <img 
                    src="/images/3d-printing-hero.jpg" 
                    alt="3D Printing Services"
                    style={{ 
                      width: '100%', 
                      maxWidth: '500px', 
                      height: 'auto',
                      borderRadius: '12px',
                      boxShadow: `0 8px 32px ${withOpacity(COLORS.primary.main, 0.15)}`
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Container maxWidth="lg" id="features">
          <Box sx={{ mb: 8 }}>
            <Typography variant="h3" textAlign="center" fontWeight={700} color={COLORS.text.primary} mb={2}>
              Why Choose Our 3D Printing Services?
            </Typography>
            <Typography variant="h6" textAlign="center" color={COLORS.text.secondary} mb={6} sx={{ maxWidth: '600px', mx: 'auto' }}>
              Professional quality, competitive pricing, and fast turnaround times for all your 3D printing needs.
            </Typography>
            
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%',
                      border: `1px solid ${COLORS.border.light}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 8px 24px ${withOpacity(COLORS.primary.main, 0.12)}`,
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Box sx={{ 
                        color: COLORS.primary.main, 
                        mb: 2,
                        '& svg': { fontSize: '3rem' }
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" fontWeight={600} color={COLORS.text.primary} mb={2}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color={COLORS.text.secondary} sx={{ lineHeight: 1.6 }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Materials Section */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h3" textAlign="center" fontWeight={700} color={COLORS.text.primary} mb={2}>
              Available Materials
            </Typography>
            <Typography variant="h6" textAlign="center" color={COLORS.text.secondary} mb={6}>
              Choose from our wide range of high-quality 3D printing materials
            </Typography>
            
            <Grid container spacing={3}>
              {materials.map((material, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      border: `1px solid ${COLORS.border.light}`,
                      borderRadius: 2,
                      height: '100%'
                    }}
                  >
                    <Typography variant="h6" fontWeight={600} color={COLORS.primary.main} mb={1}>
                      {material.name}
                    </Typography>
                    <Typography variant="body2" color={COLORS.text.secondary}>
                      {material.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Applications Section */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h3" textAlign="center" fontWeight={700} color={COLORS.text.primary} mb={2}>
              Applications
            </Typography>
            <Typography variant="h6" textAlign="center" color={COLORS.text.secondary} mb={6}>
              Perfect for various industries and use cases
            </Typography>
            
            <Grid container spacing={2}>
              {applications.map((app, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2,
                      border: `1px solid ${COLORS.border.light}`,
                      borderRadius: 2,
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: COLORS.primary.light,
                        borderColor: COLORS.primary.main
                      }
                    }}
                  >
                    <Typography variant="body1" fontWeight={500} color={COLORS.text.primary}>
                      {app}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>

        {/* Quote Calculator Section */}
        <Box id="quote-calculator" sx={{ backgroundColor: COLORS.background.paper, py: 8 }}>
          <Container maxWidth="lg">
            <Typography variant="h3" textAlign="center" fontWeight={700} color={COLORS.text.primary} mb={2}>
              Get Instant Quote
            </Typography>
            <Typography variant="h6" textAlign="center" color={COLORS.text.secondary} mb={6}>
              Upload your STL files and get an instant quote for your 3D printing project
            </Typography>
            
            <ThreeDPrintingQuote />
          </Container>
        </Box>

        {/* Contact Section */}
        <Container maxWidth="lg">
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight={700} color={COLORS.text.primary} mb={2}>
              Need Custom Solutions?
            </Typography>
            <Typography variant="h6" color={COLORS.text.secondary} mb={4} sx={{ maxWidth: '600px', mx: 'auto' }}>
              Have a complex project or need consultation? Our experts are here to help you bring your ideas to life.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<ContactMail />}
              sx={{ 
                backgroundColor: COLORS.primary.main,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              Contact Our Experts
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ThreeDPrinting;